import { useGoogleRecaptchaV2 } from '@sgcmpteltd/uikit-next';
import { Checkbox } from '@sgcmpteltd/uikit-react';
import {
  Button,
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_CONTAINED,
} from '@sgcmpteltd/uikit-react/components/button';
import { useRef, useState, useTransition } from 'react';

import { TextLink } from '@/components/text-link';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/configs/google/recaptcha';

import type { OnGoogleRecaptchSuccess } from '../types';
import { SHORTLIST_CHECKBOX_SIZE } from './constants';
import styles from './styles.module.scss';
import type { Component, ShortlistValueCheckMap } from './types';

export const Step: Component = ({
  alertPageLink,
  checkList,
  email,
  title,
  onSave,
}) => {
  const [isRecaptchaPending, setIsRecaptchaPending] = useState(false);
  const [
    shortlistValueCheckMap,
    setShortlistValueCheckMap,
  ] = useState<ShortlistValueCheckMap>(() => (
    checkList.reduce((carry, { value, checked }) => Object.assign(
      carry,
      {
        [value]: checked,
      },
    ), {})
  ));
  const [
    isShortlistConfigSavePending,
    startShortlistConfigSaveTransition,
  ] = useTransition();

  const googleRecaptchaWidgetElRef = useRef<HTMLDivElement | null>(null);
  const onGoogleRecaptchSuccessRef = useRef<OnGoogleRecaptchSuccess | null>(
    null,
  );
  const {
    reset: resetRecaptcha,
    execute: executeRecaptcha,
  } = useGoogleRecaptchaV2({
    siteKey: GOOGLE_RECAPTCHA_SITE_KEY ?? '',
    widgetElRef: googleRecaptchaWidgetElRef,
    optionsRef: useRef({
      size: 'invisible',
    }),
    onSuccess: () => {
      setIsRecaptchaPending(false);

      onGoogleRecaptchSuccessRef.current?.();
    },
    onExpired: () => {
      setIsRecaptchaPending(false);
    },
    onError: () => {
      setIsRecaptchaPending(false);
    },
  });

  return (
    <div
      className={styles.containerRoot}>
      <span
        className={styles.textStepTitle}>
        {title}
      </span>
      <ul
        className={styles.listShortlistCheck}>
        {
          checkList.map(({
            text,
            value,
          }) => {
            const checkboxId = `promotion-details-modal-${value}`;

            return (
              <li
                key={value}
                className={styles.containerShortlistCheck}>
                <Checkbox
                  checked={shortlistValueCheckMap[value]}
                  id={checkboxId}
                  size={SHORTLIST_CHECKBOX_SIZE}
                  value={value}
                  onChange={({
                    target: {
                      checked: isChecked,
                    },
                  }) => {
                    setShortlistValueCheckMap(previousMap => ({
                      ...previousMap,
                      [value]: isChecked,
                    }));
                  }} />
                <label
                  htmlFor={checkboxId}>
                  {text}
                </label>
              </li>
            );
          })
        }
      </ul>
      <div
        ref={googleRecaptchaWidgetElRef} />
      {
        email && (
          <span
            className={styles.textStepMessage}>
            Your alert(s) will be sent to
            {' '}
            {email}
          </span>
        )
      }
      <div
        className={styles.containerSaveButton}>
        <Button
          color={BUTTON_COLOR_RED}
          disabled={isShortlistConfigSavePending || isRecaptchaPending}
          variant={BUTTON_VARIANT_CONTAINED}
          onClick={() => {
            if (!onSave) {
              return;
            }

            onGoogleRecaptchSuccessRef.current = () => {
              startShortlistConfigSaveTransition(async () => {
                await onSave({
                  checkMap: shortlistValueCheckMap,
                });
              });
            };

            resetRecaptcha();
            executeRecaptcha();
            setIsRecaptchaPending(true);
          }}>
          Save
        </Button>
      </div>
      <span
        className={styles.textStepMessage}>
        Or go to my
        {' '}
        <TextLink
          href={alertPageLink}
          text="email alerts page" />
      </span>
    </div>
  );
};
